import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { QrImage } from '../components/QrImage';

interface Member {
  id: string;
  role: string;
  user_id: string;
  full_name: string | null;
  can_finance: boolean;
  is_temp: boolean;
  expires_at: string | null;
  phone: string | null;
}

interface Invite {
  id: string;
  email: string;
  role: string;
}

interface StaffReq {
  id: string;
  name: string;
  phone: string | null;
}

const INVITE_ROLES = ['manager', 'waiter', 'kitchen', 'hostess'];

export function TeamPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const canManage = restaurant?.role === 'owner' || restaurant?.role === 'manager';
  const isOwner = restaurant?.role === 'owner';

  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [staffReqs, setStaffReqs] = useState<StaffReq[]>([]);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('waiter');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data: m } = await supabase
      .from('restaurant_members')
      .select('id, role, user_id, can_finance, is_temp, expires_at, phone, profiles(full_name)')
      .eq('restaurant_id', restaurant.id);
    if (m) {
      setMembers(
        m.map((row) => ({
          id: row.id as string,
          role: row.role as string,
          user_id: row.user_id as string,
          can_finance: Boolean(row.can_finance),
          is_temp: Boolean(row.is_temp),
          expires_at: (row.expires_at as string | null) ?? null,
          phone: (row.phone as string | null) ?? null,
          full_name: (row.profiles as unknown as { full_name: string | null } | null)?.full_name ?? null
        }))
      );
    }
    const { data: inv } = await supabase
      .from('restaurant_invites')
      .select('id, email, role')
      .eq('restaurant_id', restaurant.id)
      .eq('status', 'pending');
    if (inv) setInvites(inv as Invite[]);

    const { data: reqs } = await supabase
      .from('staff_access_requests')
      .select('id, name, phone')
      .eq('restaurant_id', restaurant.id)
      .eq('status', 'pending')
      .order('created_at');
    if (reqs) setStaffReqs(reqs as StaffReq[]);

    const { data: r } = await supabase.from('restaurants').select('staff_enroll_token').eq('id', restaurant.id).single();
    if (r) setEnrollToken((r.staff_enroll_token as string | null) ?? null);
  }, [restaurant]);

  useEffect(() => {
    void reload();
    if (!restaurant) return;
    const ch = supabase
      .channel(`team-${restaurant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_access_requests', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [reload, restaurant]);

  async function handleInvite(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!restaurant) return;
    const { error: err } = await supabase.from('restaurant_invites').insert({
      restaurant_id: restaurant.id,
      email: email.trim().toLowerCase(),
      role,
      invited_by: user?.id ?? null
    });
    if (err) {
      setError(err.message);
      return;
    }
    setEmail('');
    void reload();
  }

  async function revoke(id: string) {
    await supabase.from('restaurant_invites').update({ status: 'revoked' }).eq('id', id);
    void reload();
  }

  async function toggleFinance(m: Member) {
    await supabase.from('restaurant_members').update({ can_finance: !m.can_finance }).eq('id', m.id);
    void reload();
  }

  async function approveStaff(id: string) {
    await supabase.rpc('staff_approve_request', { p_request_id: id });
    void reload();
  }
  async function rejectStaff(id: string) {
    await supabase.rpc('staff_reject_request', { p_request_id: id });
    void reload();
  }
  async function regenToken() {
    if (!restaurant) return;
    const { data } = await supabase.rpc('regenerate_staff_token', { p_rid: restaurant.id });
    if (data) setEnrollToken(data as string);
  }
  async function removeMember(id: string) {
    await supabase.from('restaurant_members').delete().eq('id', id);
    void reload();
  }
  function minsLeft(iso: string | null): number {
    if (!iso) return 0;
    return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page">
      <h1 className="page__title">{t('team.title')}</h1>

      <h2 className="section-title">{t('team.members')}</h2>
      <ul className="list">
        {members.filter((m) => !m.is_temp).map((m) => (
          <li key={m.id} className="list__row">
            <span>
              {m.full_name || '—'}
              {m.user_id === user?.id && <em className="tag"> ({t('team.you')})</em>}
              {m.role === 'manager' && m.can_finance && <em className="tag"> 💳 {t('team.finance_on')}</em>}
            </span>
            <span className="row-actions">
              {isOwner && m.role === 'manager' && (
                <button type="button" className="link-button" onClick={() => void toggleFinance(m)}>
                  {m.can_finance ? t('team.revoke_finance') : t('team.grant_finance')}
                </button>
              )}
              <span className="badge">{t(`team.role_${m.role}`)}</span>
            </span>
          </li>
        ))}
      </ul>

      {canManage && (
        <>
          <h2 className="section-title">{t('team.shift_title')}</h2>
          {enrollToken && (
            <div className="qr-card" style={{ maxWidth: 220, marginBottom: 16 }}>
              <QrImage value={`${window.location.origin}/garcom/${enrollToken}`} size={150} />
              <span className="muted-note" style={{ textAlign: 'center' }}>{t('team.shift_qr_hint')}</span>
              <button type="button" className="link-button" onClick={() => void regenToken()}>{t('team.regenerate')}</button>
            </div>
          )}
          <h3 className="section-title">{t('team.pending_staff')}</h3>
          {staffReqs.length === 0 ? (
            <p className="muted-note">{t('team.no_pending_staff')}</p>
          ) : (
            <ul className="list">
              {staffReqs.map((s) => (
                <li key={s.id} className="list__row">
                  <span>{s.name}{s.phone && <span className="dimmed"> · {s.phone}</span>}</span>
                  <span className="row-actions">
                    <button type="button" className="link-button" onClick={() => void approveStaff(s.id)}>{t('team.approve')}</button>
                    <button type="button" className="link-button" onClick={() => void rejectStaff(s.id)}>{t('team.reject')}</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {members.some((m) => m.is_temp) && (
            <>
              <h3 className="section-title">{t('team.temp_members')}</h3>
              <ul className="list">
                {members.filter((m) => m.is_temp).map((m) => (
                  <li key={m.id} className="list__row">
                    <span>
                      {m.full_name || '—'}{m.phone && <span className="dimmed"> · {m.phone}</span>}
                      <em className="tag" style={{ marginLeft: 6 }}>{t('team.expires_in')} {minsLeft(m.expires_at)} {t('team.min')}</em>
                    </span>
                    <button type="button" className="link-button" onClick={() => void removeMember(m.id)}>{t('team.remove')}</button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h2 className="section-title">{t('team.invite_title')}</h2>
          <form onSubmit={handleInvite} className="form">
            <div className="form-row">
              <label className="field" style={{ flex: 2 }}>
                <span>{t('team.invite_email')}</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="field">
                <span>{t('team.invite_role')}</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  {INVITE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`team.role_${r}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="btn btn--primary">
              {t('team.invite_cta')}
            </button>
            <p className="muted-note">{t('team.invite_hint')}</p>
          </form>

          <h2 className="section-title">{t('team.pending')}</h2>
          {invites.length === 0 ? (
            <p className="muted-note">{t('team.no_pending')}</p>
          ) : (
            <ul className="list">
              {invites.map((i) => (
                <li key={i.id} className="list__row">
                  <span>
                    {i.email} <span className="badge">{t(`team.role_${i.role}`)}</span>
                  </span>
                  <button type="button" className="link-button" onClick={() => void revoke(i.id)}>
                    {t('team.revoke')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
